import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { createFundInvoicesBuyBack } from 'lib/api/funds';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import { formatDate, showAlert } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { ResellWithSuggestions, useFactoringResell } from '../../context';

const MultiBuyBack: React.FC = () => {
  const {
    openMultiBuyBack,
    setOpenMultiBuyBack,
    resellsSelected,
    setResellsSelected,
    setSelectedResellIds,
    pagination,
    fetchResellings,
  } = useFactoringResell();
  const dispatch = useDispatch();
  const [openBuyBack, setOpenBuyBack] = useState(false);
  const [loading, setLoading] = useState(false);
  const newBuybackAt = new Date(new Date().setUTCHours(12, 0, 0, 0));
  const [buybackAt, setBuybackAt] = useState<Date>(newBuybackAt);

  const validParams = () => {
    if (!buybackAt) {
      showAlert(dispatch, 'error', 'Debes seleccionar una fecha');
      return false;
    }
    return true;
  };

  const handleChangeAmountToBuyback = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const amount = event.target.value.split('.').join('').split('$').join('');
    const resell = resellsSelected.find(
      (resell: ResellWithSuggestions) =>
        String(resell.id) === event.target.name,
    );
    if (!resell) return;
    resell.suggestedAmountBuyback = Number(amount);
    setResellsSelected([...resellsSelected]);
  };

  const handleBuyBackInvoices = async () => {
    if (!validParams()) {
      return;
    }
    setOpenBuyBack(false);
    setLoading(true);
    try {
      if (!validParams()) {
        return;
      }
      setLoading(true);
      const resellsForBuyback = resellsSelected.map(
        (resell: ResellWithSuggestions) => ({
          id: resell.id,
          amount: resell.suggestedAmountBuyback,
          buybackAt,
        }),
      );
      await createFundInvoicesBuyBack(dispatch, {
        resellsForBuyback: resellsForBuyback,
      });
      setOpenBuyBack(false);
      showAlert(dispatch, 'success', 'Facturas recompradas correctamente');
      void fetchResellings({
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
      setOpenMultiBuyBack(false);
      setResellsSelected([]);
      setSelectedResellIds([]);
    } catch (error) {
      setOpenMultiBuyBack(false);
      showAlert(dispatch, 'error', 'Error al recomprar facturas');
      setSelectedResellIds([]);
    } finally {
      setLoading(false);
    }
  };

  const DateSelector = () => {
    return (
      <div>
        <TextField
          label="Fecha"
          value={buybackAt ? formatDate(buybackAt) : null}
          type="date"
          onChange={(event) =>
            setBuybackAt(
              new Date(new Date(event.target.value).setUTCHours(12, 0, 0, 0)),
            )
          }
        />
      </div>
    );
  };

  return (
    <Dialog maxWidth="lg" fullWidth open={openMultiBuyBack}>
      <div style={{ padding: 20, minHeight: 200 }}>
        <div>
          <HeaderWithActions title="Recomprar facturas" item={DateSelector()} />
          <div style={{ padding: '10px 0px' }}>
            <EnhancedTable
              data={resellsSelected}
              dataHeaders={[
                {
                  key: 'id',
                  label: 'Id',
                  type: 'numeric-id',
                },
                {
                  key: 'folio',
                  label: 'Folio',
                },
                {
                  key: 'businessName',
                  label: 'Cliente',
                },
                {
                  key: 'stakeholderName',
                  label: 'Deudor',
                },
                {
                  key: 'fundName',
                  label: 'Fondo',
                },
                {
                  key: 'amount',
                  label: 'Monto documento',
                  type: 'money',
                },
                {
                  key: 'documentAmountRemaining',
                  label: 'Saldo documento financiado fondo',
                  type: 'money',
                },
                {
                  key: 'suggestedAmountBuyback',
                  label: 'Monto a recomprar',
                  type: 'money',
                  handleChange: handleChangeAmountToBuyback,
                },
              ]}
            />
          </div>
          <DialogActions>
            <CustomButton
              onClick={() => setOpenMultiBuyBack(false)}
              color="secondary"
            >
              Salir
            </CustomButton>
            <CustomButton
              onClick={() => setOpenBuyBack(true)}
              color="primary"
              loading={loading}
            >
              Recomprar
            </CustomButton>
          </DialogActions>
        </div>
      </div>
      <Dialog open={openBuyBack} onClose={() => setOpenBuyBack(false)}>
        <HeaderWithActions title="Recomprar" />
      </Dialog>
      <Dialog open={openBuyBack} onClose={() => setOpenBuyBack(false)}>
        <DialogTitle>
          <Typography variant="h6">Recomprar Facturas</Typography>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <div style={{ minHeight: '100px' }}>
              <CenteredCircularProgress type="dialog" />
            </div>
          ) : (
            <Typography variant="body1">
              Estás seguro de querer recomprar las facturas?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={() => setOpenBuyBack(false)}
            color="secondary"
            disabled={loading}
          >
            Cancelar
          </CustomButton>
          <CustomButton
            onClick={() => handleBuyBackInvoices()}
            color="primary"
            disabled={loading}
          >
            Confirmar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default MultiBuyBack;
